import React from "react";
import aduanaTheme from "../../components/DuelSkin/aduanaTheme";

const { labels, formatOperativeDate } = aduanaTheme;

// design.md D7 — surfaces only 5 of the 7 get_user_stats fields; draws and
// totalpoints stay in the fixture (a 7-cell strip would fragment on a 900px
// panel), no contract change needed to add them later.
const STAT_FIELDS = [
  { key: "totalduels", label: labels.historyStatTotalLabel },
  { key: "wins", label: labels.historyStatWinsLabel },
  { key: "losses", label: labels.historyStatLossesLabel },
  { key: "winrate", label: labels.historyStatWinrateLabel },
  { key: "currentstreak", label: labels.historyStatStreakLabel },
];

const RESULT_LABEL = {
  won: labels.historyResultWonLabel,
  lost: labels.historyResultLostLabel,
  draw: labels.historyResultDrawLabel,
};

/**
 * DuelsHistoryList — presentational ledger of finished duels for the
 * "Historial" tab on `/duels` (design.md duel-history-provisional, D6/D7).
 *
 * Purely presentational: consumes `stats`/`duels` via props — mirroring
 * `DuelsList`'s contract — rather than importing `DUEL_HISTORY_MOCK`
 * directly, so `DuelsPage` (Phase 4) stays the single place that decides
 * where the data comes from. `duels` are expected in the RAW
 * `list_my_duels(status:"finished")` shape (design.md D3, NOT
 * `normalizeChallenge`d) — no `coursename` is available until the plugin
 * adds one.
 *
 * Reduced visual emphasis vs. `DuelsList`'s cards (design.md D6): stacked
 * ledger rows (`.duel-skin-review-item.duel-skin-history-item`, PR1 CSS
 * deltas — border alpha 0.22→0.12, transparent background, dimmed text,
 * 13px callsign, no hover transform), never `.duel-skin-rival-card`. Rows
 * hold NO `onClick`/`onSelect` prop and render NO
 * `.duel-skin-action-btn` — spec `duel-history-provisional`, "History Cards Are
 * Not Clickable Into Battle": there is no wiring here that could open a
 * battle modal, navigate, or write `activeChallengeStorage`.
 *
 * The invented per-duel result is read only via `duel.__mockOnly?.result`
 * ("won"|"lost"|"draw") to pick the chip variant — design.md D4, spec
 * "Static Mock Data Contract for History" ("invented ... result field MUST
 * be marked mock-only"). All copy/labels come from `ADUANA_LABELS`, no
 * inline literal — spec "All Copy and Styling Come From the Theme".
 *
 * @param {Object} props
 * @param {Object} [props.stats] - Aggregate stats shaped like the
 *   `get_user_stats` response (only 5 of 7 fields surfaced, design.md D7):
 *   totalduels, wins, losses, winrate, currentstreak.
 * @param {Array} [props.duels] - Raw `list_my_duels(status:"finished")`
 *   rows, each optionally carrying `__mockOnly.result`.
 */
export const DuelsHistoryList = ({ stats, duels, isMock, onSelect }) => {
  const hasDuels = Array.isArray(duels) && duels.length > 0;
  const isMockData = isMock !== undefined ? isMock : duels?.some((d) => d.__mockOnly);

  return (
    <div>
      {isMockData && <p className="duel-skin-tag">{labels.historyMockNotice}</p>}

      {!hasDuels ? (
        <div style={{ textAlign: "center", padding: "48px 0" }}>
          <div className="duel-skin-badge-big">
            <span>{"⚔"}</span>
          </div>
          <p className="duel-skin-hero-face" style={{ fontSize: "18px", margin: "0 0 8px" }}>
            {labels.noHistoryLabel}
          </p>
          <p style={{ color: "var(--duel-skin-text-dim)", maxWidth: "40ch", margin: "0 auto" }}>
            {labels.noHistorySubtext}
          </p>
        </div>
      ) : (
        <>
          {stats && (
            <div className="duel-skin-history-stats">
              {STAT_FIELDS.map((field) => (
                <div key={field.key} className="duel-skin-history-stat">
                  <span className="duel-skin-history-stat-label">{field.label}</span>
                  <span className="duel-skin-history-stat-value">{stats[field.key]}</span>
                </div>
              ))}
            </div>
          )}

          <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
            {duels.map((duel) => {
              const result = duel.result || duel.__mockOnly?.result;
              const timecreated = duel.timecreated ?? duel.raw?.timecreated;
              const myprogress = duel.myprogress ?? duel.raw?.myprogress ?? duel.myAttempt?.status;
              const opponentprogress =
                duel.opponentprogress ?? duel.raw?.opponentprogress ?? duel.opponentAttempt?.status;
              const opponentName =
                duel.opponent?.fullname ||
                duel.raw?.opponent?.fullname ||
                duel.rival?.fullname ||
                labels.you;

              return (
                <div
                  key={duel.id}
                  className={`duel-skin-review-item duel-skin-history-item${
                    onSelect ? " is-clickable" : ""
                  }`}
                  onClick={() => onSelect && onSelect(duel)}
                  style={onSelect ? { cursor: "pointer" } : undefined}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      gap: "12px",
                    }}
                  >
                    <div>
                      <div className="duel-skin-callsign">{opponentName}</div>
                      <div className="duel-skin-time">{formatOperativeDate(timecreated)}</div>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                      <span
                        style={{
                          fontFamily: "ui-monospace, monospace",
                          color: "var(--duel-skin-text-dim)",
                        }}
                      >
                        {typeof duel.myscore === "number" && typeof duel.opponentscore === "number"
                          ? `${duel.myscore} - ${duel.opponentscore}`
                          : `${myprogress || "—"} - ${opponentprogress || "—"}`}
                      </span>
                      {result && (
                        <span className={`duel-skin-result-chip duel-skin-result-chip--${result}`}>
                          {RESULT_LABEL[result]}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

export default DuelsHistoryList;
